// Tour Service - Handles all API calls to Supabase Edge Functions
import { SUPABASE_CONFIG, supabase } from '../config/supabase';

interface GeocodeResponse {
  streetName: string;
  plusCode: string;
  area: string;
  city: string;
  country: string;
  formattedAddress: string;
}

interface Place {
  name: string;
  type: string[];
  address: string;
  placeId: string;
  rating?: number;
  userRatingsTotal?: number;
  travelDistance?: number; // Travel distance in meters
  travelDuration?: number; // Travel time in seconds
}

interface TourResponse {
  tourId: string;
  userId?: string;
  narration: string;
  title: string;
  description: string;
  estimatedDuration: number;
  distance: string;
  sources?: string[];
  audioUrl?: string;
  audioDuration?: number;
  audioFileSize?: number;
  audioStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  researchStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  locationData?: any;
  createdAt?: string;
  saveCount?: number;
  creator?: {
    name: string | null;
    avatarUrl: string | null;
  };
}

interface AudioGenerationResponse {
  success: boolean;
  audioUrl?: string;
  audioDuration?: number;
  audioFileSize?: number;
  error?: string;
}

class TourService {
  private baseUrl: string;
  private headers: HeadersInit;

  constructor() {
    this.baseUrl = SUPABASE_CONFIG.url;
    this.headers = {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_CONFIG.anonKey,
      'Authorization': `Bearer ${SUPABASE_CONFIG.anonKey}`
    };
  }

  async geocodeLocation(latitude: number, longitude: number): Promise<GeocodeResponse> {
    try {
      const response = await fetch(`${this.baseUrl}${SUPABASE_CONFIG.endpoints.geocode}`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({ latitude, longitude })
      });

      if (!response.ok) {
        throw new Error(`Geocoding failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Geocoding error:', error);
      throw error;
    }
  }

  async getPlacesOnStreet(streetName: string, latitude: number, longitude: number): Promise<{ places: Place[], total: number, debug?: any }> {
    try {
      const response = await fetch(`${this.baseUrl}${SUPABASE_CONFIG.endpoints.getPlaces}`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({ streetName, latitude, longitude })
      });

      if (!response.ok) {
        throw new Error(`Get places failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Get places error:', error);
      throw error;
    }
  }

  // ============================================
  // TOUR RETRIEVAL
  // ============================================

  async getTour(tourId: string): Promise<TourResponse> {
    try {
      const response = await fetch(`${this.baseUrl}${SUPABASE_CONFIG.endpoints.getTour}?tourId=${tourId}`, {
        method: 'GET',
        headers: this.headers
      });

      if (!response.ok) {
        throw new Error(`Get tour failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Get tour error:', error);
      throw error;
    }
  }

  async listTours(
    limit: number = 20, 
    offset: number = 0, 
    options?: {
      userId?: string;
      latitude?: number;
      longitude?: number;
      sortByDistance?: boolean;
      audioCompleted?: boolean;
    }
  ): Promise<{ tours: any[], total: number, sortedByDistance?: boolean }> {
    try {
      let url = `${this.baseUrl}${SUPABASE_CONFIG.endpoints.listTours}?limit=${limit}&offset=${offset}`;
      
      if (options?.userId) {
        url += `&userId=${options.userId}`;
      }
      
      if (options?.latitude && options?.longitude) {
        url += `&latitude=${options.latitude}&longitude=${options.longitude}`;
        if (options?.sortByDistance) {
          url += `&sortByDistance=true`;
        }
      }

      if (options?.audioCompleted) {
        url += `&audioCompleted=true`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: this.headers
      });

      if (!response.ok) {
        throw new Error(`List tours failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('List tours error:', error);
      throw error;
    }
  }

  // ============================================
  // TOUR MANAGEMENT
  // ============================================

  async deleteTour(tourId: string): Promise<void> {
    try {
      // Use authenticated headers for proper RLS policy enforcement
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.baseUrl}/rest/v1/tours?tour_id=eq.${tourId}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Delete tour failed: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Delete tour error:', error);
      throw error;
    }
  }

  // ============================================
  // PERPLEXITY RESEARCH (PRIMARY METHOD)
  // ============================================

  /**
   * Generate a comprehensive tour using Perplexity's Sonar API.
   * This is a synchronous call that returns the complete tour.
   * Typically takes 30-90 seconds for comprehensive research.
   * 
   * @param locationData - Location information
   * @param places - User-selected places to focus on
   * @param interests - User interests
   * @param nearbyPlaces - Other interesting places nearby (not selected but worth mentioning)
   * @param userId - User ID to associate the tour with
   * @param onProgress - Callback for progress updates
   * @param tourId - Optional tour ID to update an existing pending tour
   */
  async generatePerplexityTour(
    locationData: GeocodeResponse & { latitude: number; longitude: number },
    places: Place[],
    interests: string[],
    nearbyPlaces: Place[] = [],
    userId?: string,
    onProgress?: (status: string, progress?: string) => void,
    tourId?: string
  ): Promise<TourResponse> {
    try {
      onProgress?.('starting', 'Initiating Perplexity research...');
      
      const response = await fetch(`${this.baseUrl}${SUPABASE_CONFIG.endpoints.perplexityResearch}`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({ locationData, places, interests, nearbyPlaces, userId, tourId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Research failed: ${response.statusText}`);
      }

      onProgress?.('completed', 'Research complete! Your tour is ready.');
      return await response.json();
    } catch (error) {
      console.error('Perplexity research error:', error);
      throw error;
    }
  }

  // ============================================
  // AUDIO GENERATION
  // ============================================

  /**
   * Manually trigger audio generation for a tour.
   * Usually audio is generated automatically when the tour is created,
   * but this can be used to retry if it failed.
   */
  async generateAudio(tourId: string): Promise<AudioGenerationResponse> {
    try {
      // IMPORTANT: Update database status to 'processing' BEFORE calling the Edge Function
      // This ensures the state persists even if the user navigates away
      console.log('🔄 Updating audio status to processing for tour:', tourId);
      
      const { error: updateError } = await supabase
        .from('tours')
        .update({ 
          audio_status: 'processing',
          updated_at: new Date().toISOString()
        })
        .eq('tour_id', tourId);

      if (updateError) {
        console.error('❌ Failed to update audio status to processing:', updateError);
        // Continue anyway - the Edge Function will also set it to processing
      } else {
        console.log('✅ Audio status updated to processing');
      }

      // Now trigger the Edge Function (which will continue processing even if user navigates away)
      const response = await fetch(`${this.baseUrl}${SUPABASE_CONFIG.endpoints.generateAudio}`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({ tourId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Audio generation failed: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Audio generation error:', error);
      throw error;
    }
  }

  /**
   * Poll for audio status until it's ready or fails.
   * Returns when audio is completed or after timeout.
   */
  async waitForAudio(
    tourId: string, 
    options: { 
      pollInterval?: number; 
      maxAttempts?: number;
      onStatusChange?: (status: string) => void;
    } = {}
  ): Promise<TourResponse> {
    const { 
      pollInterval = 3000, 
      maxAttempts = 60, // 3 minutes max with 3s interval
      onStatusChange 
    } = options;

    let attempts = 0;
    
    while (attempts < maxAttempts) {
      const tour = await this.getTour(tourId);
      
      if (onStatusChange) {
        onStatusChange(tour.audioStatus || 'pending');
      }
      
      if (tour.audioStatus === 'completed') {
        return tour;
      }
      
      if (tour.audioStatus === 'failed') {
        throw new Error('Audio generation failed');
      }
      
      attempts++;
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
    
    throw new Error('Audio generation timed out');
  }

  // ============================================
  // SAVED TOURS
  // ============================================

  /**
   * Get authenticated headers with user's JWT token
   */
  private async getAuthHeaders(): Promise<HeadersInit> {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      throw new Error('User not authenticated');
    }

    return {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_CONFIG.anonKey,
      'Authorization': `Bearer ${session.access_token}`
    };
  }

  /**
   * Save a tour to the user's collection
   */
  async saveTour(tourId: string, userId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.baseUrl}/rest/v1/saved_tours`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ 
          tour_id: tourId,
          user_id: userId
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to save tour');
      }

      return { success: true };
    } catch (error) {
      console.error('Save tour error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to save tour'
      };
    }
  }

  /**
   * Unsave a tour from the user's collection
   */
  async unsaveTour(tourId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.baseUrl}/rest/v1/saved_tours?tour_id=eq.${tourId}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to unsave tour');
      }

      return { success: true };
    } catch (error) {
      console.error('Unsave tour error:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to unsave tour'
      };
    }
  }

  /**
   * Check if a tour is saved by the current user
   */
  async isTourSaved(tourId: string): Promise<boolean> {
    try {
      const headers = await this.getAuthHeaders();
      
      const response = await fetch(`${this.baseUrl}/rest/v1/saved_tours?tour_id=eq.${tourId}&select=id`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data && data.length > 0;
    } catch (error) {
      console.error('Check saved tour error:', error);
      return false;
    }
  }

  /**
   * Get all saved tours for the current user
   */
  async getSavedTours(): Promise<{ tours: any[]; total: number }> {
    try {
      const headers = await this.getAuthHeaders();
      
      // First get the saved tour IDs
      const savedResponse = await fetch(`${this.baseUrl}/rest/v1/saved_tours?select=tour_id&order=saved_at.desc`, {
        method: 'GET',
        headers
      });

      if (!savedResponse.ok) {
        console.error('Failed to get saved tours list');
        return { tours: [], total: 0 };
      }

      const savedData = await savedResponse.json();
      
      if (!savedData || savedData.length === 0) {
        return { tours: [], total: 0 };
      }

      // Get the tour IDs
      const tourIds = savedData.map((item: any) => item.tour_id);
      
      // Fetch the full tour details for these IDs
      const toursResponse = await fetch(
        `${this.baseUrl}/rest/v1/tours?tour_id=in.(${tourIds.map((id: string) => `"${id}"`).join(',')})&select=tour_id,user_id,title,description,street_name,city,country,estimated_duration,distance,audio_status,created_at,view_count,save_count,latitude,longitude`,
        {
          method: 'GET',
          headers
        }
      );

      if (!toursResponse.ok) {
        console.error('Failed to get tour details');
        return { tours: [], total: 0 };
      }

      const toursData = await toursResponse.json();
      
      // Map to expected format
      const tours = toursData.map((tour: any) => ({
        tourId: tour.tour_id,
        userId: tour.user_id,
        title: tour.title,
        description: tour.description,
        streetName: tour.street_name,
        city: tour.city,
        country: tour.country,
        estimatedDuration: tour.estimated_duration,
        distance: tour.distance,
        audioStatus: tour.audio_status,
        createdAt: tour.created_at,
        viewCount: tour.view_count || 0,
        saveCount: tour.save_count || 0,
        latitude: tour.latitude,
        longitude: tour.longitude
      }));
      
      return {
        tours,
        total: tours.length
      };
    } catch (error) {
      console.error('Get saved tours error:', error);
      // Return empty array on error instead of throwing
      return { tours: [], total: 0 };
    }
  }
}

const tourService = new TourService();
export default tourService; 